import React, { useEffect, useState } from "react";
import { Badge, Button, Modal, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { useSelector } from "react-redux";
import styles from "./AgreementDetail.module.css";
import ContractDetail from "./ContractDetail";
import PartyDetail from "./PartyDetail";
import { formatDate, formatTime } from "../../utilities/utilities";
import {
  agreementStatusColors,
  getStatusColor,
  getStatusText,
} from "../../utilities/enums";
import {
  deleteAgreement,
  deleteAgreementBulk,
  getAgreementDetails,
} from "../../utilities/apis/agreements";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import colors from "../../utilities/design";
import { selectUserData } from "../../redux/selectors";
import UserSelector from "../UserSelector/UserSelector";
import { getUserRolesUsingRoleId } from "../../utilities/apis/serviceProvider";
import EditSignatoryDetails from "./EditSignatoryDetail";
import CloseAgreementModal from "../CloseAgreementModal";
import {
  IconFileText,
  IconEdit,
  IconTrash,
  IconLock,
  IconUsers,
} from "@tabler/icons-react";

function AgreementDetail({ agreementData }) {
  const navigate = useNavigate();
  const userDetails = useSelector(selectUserData);
  const currentUser = useSelector(selectUserData) || {};
  const agreementId = agreementData?.agreementId;
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const { search, pathname } = useLocation();

  const [userRolesIdData, setUserRolesIdData] = useState({
    userRoles: {
      Initiator: [],
      Editor: [],
      Reviewer: [],
      Approver: [],
      SigningAuthority: [],
    },
  });
  const flowJson = agreementData.wholeAgreement.flowJson;

  const checkUserCompletionInFlow = (key, userId) => {
    // NEW: If the entire agreement is SIGNED or COMPLETED,
    // we treat the Signing Authority (role "7") as complete automatically.
    if (
      (agreementData.agreementStatus === "SIGNED" ||
        agreementData.agreementStatus === "COMPLETED") &&
      key === "7"
    ) {
      return true;
    }
    if (flowJson && flowJson[key]) {
      const userEntry = flowJson[key].find((entry) => entry.userId === userId);
      if (userEntry) {
        return userEntry.isComplete;
      }
    }
    return false;
  };

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const roles = {
          Initiator: (await getUserRolesUsingRoleId(3)).data.result,
          Editor: (await getUserRolesUsingRoleId(4)).data.result,
          Reviewer: (await getUserRolesUsingRoleId(5)).data.result,
          Approver: (await getUserRolesUsingRoleId(6)).data.result,
          SigningAuthority: (await getUserRolesUsingRoleId(7)).data.result,
        };

        setUserRolesIdData((prevData) => ({
          ...prevData,
          userRoles: roles,
        }));
      } catch (error) {
        console.error("Error fetching user roles:", error);
      }
    };

    fetchUserRoles();
  }, [agreementId]);
  const [approvers, setApprovers] = useState({});
  const [reviewers, setReviewer] = useState({});
  const [editors, setEditor] = useState({});
  const [initiators, setInitiator] = useState({});
  const [signAuths, setSignAuth] = useState({});
  const [showEditSignatoryModal, setShowEditSignatoryModal] = useState(false);

  useEffect(() => {
    const fetchUserRoles = () => {
      if (
        agreementData &&
        userRolesIdData &&
        !agreementData.data.json.isLegacy &&
        agreementData.wholeAgreement.flowJson
      ) {
        const newReviewers = {};
        if (agreementData.wholeAgreement.flowJson["5"]?.length > 0) {
          agreementData.wholeAgreement.flowJson["5"]?.forEach((item) => {
            const reviewer = userRolesIdData.userRoles.Reviewer.find(
              (role) => role.id === item.userId,
            );
            if (reviewer) {
              newReviewers[item.userId] = reviewer;
            }
          });
          setReviewer((prevReviewers) => ({
            ...prevReviewers,
            ...newReviewers,
          }));
        }
        const newApprovers = {};
        if (agreementData.wholeAgreement.flowJson["6"]?.length > 0) {
          agreementData.wholeAgreement.flowJson["6"]?.forEach((item) => {
            const approver = userRolesIdData.userRoles.Approver.find(
              (role) => role.id === item.userId,
            );
            if (approver) {
              newApprovers[item.userId] = approver;
            }
          });
          setApprovers((prevApprovers) => ({
            ...prevApprovers,
            ...newApprovers,
          }));
        }

        const newEditors = {};
        if (agreementData.wholeAgreement.flowJson["4"]?.length > 0) {
          agreementData.wholeAgreement.flowJson["4"]?.forEach((item) => {
            const editor = userRolesIdData.userRoles.Editor.find(
              (role) => role.id === item.userId,
            );
            if (editor) {
              newEditors[item.userId] = editor;
            }
          });
          setEditor((prevEditor) => ({ ...prevEditor, ...newEditors }));
        }
        const newInitiators = {};
        if (agreementData.wholeAgreement.flowJson["3"]?.length > 0) {
          agreementData.wholeAgreement.flowJson["3"]?.forEach((item) => {
            const initiator = userRolesIdData.userRoles.Initiator.find(
              (role) => role.id === item.userId,
            );
            if (initiator) {
              newInitiators[item.userId] = initiator;
            }
          });
          setInitiator((prevInitiator) => ({
            ...prevInitiator,
            ...newInitiators,
          }));
        }

        const newSignAuths = {};
        if (agreementData.wholeAgreement.flowJson["7"]?.length > 0) {
          agreementData.wholeAgreement.flowJson["7"]?.forEach((item) => {
            const signAuth = userRolesIdData.userRoles.SigningAuthority.find(
              (role) => role.id === item.userId,
            );
            if (signAuth) {
              newSignAuths[item.userId] = signAuth;
            }
          });
          setSignAuth((prevSignAuths) => ({
            ...prevSignAuths,
            ...newSignAuths,
          }));
        }
      }
    };
    fetchUserRoles();
  }, [agreementData, userRolesIdData]);

  // Ensure that agreementData and userRolesIdData are dependencies in useEffect

  // console.log("Approvers : ", approvers);

  const deleteHandler = async () => {
    setDeleteLoading(true);
    const response = await apiWrapWithErrorWithData(
      deleteAgreement({ agreementId }),
    );
    if (response?.success) {
      showNotification({
        color: "green",
        title: "Contracts",
        message: "Contract deleted successfully.",
      });
      setOpen(false);
      navigate("/app/agreements_manager");
    } else {
      showNotification({
        color: "red",
        title: "Contracts",
        message: "Unable to delete contract",
      });
    }
    setDeleteLoading(false);
  };
  console.log(agreementData);
  console.log("editors :", editors);

  useEffect(() => {
    if (new URLSearchParams(search).get("close") === "true") {
      setCloseModalOpen(true);
      // Clean the URL immediately to prevent re-triggering on refresh
      navigate(pathname, { replace: true });
    }
  }, [search, pathname, navigate]);

  const RoleCard = ({ title, users, flowKey }) => (
    <div
      className="
      bg-white
      border
      border-slate-200
      rounded-2xl
      p-5
      shadow-sm
      hover:shadow-md
      transition-all
      min-h-[220px]
      flex
      flex-col
    "
    >
      <div className="flex items-center gap-2 mb-4">
        <IconUsers size={18} color="#1b6f8d" />

        <span className="font-semibold text-[#1b6f8d] text-sm uppercase tracking-wide">
          {title}
        </span>
      </div>

      <div className="flex flex-col gap-3">
        {users && Object.keys(users).length ? (
          Object.values(users).map((user) => {
            const completed = checkUserCompletionInFlow(flowKey, user.id);

            return (
              <div
                key={user.id}
                className={`
                px-4
                py-3
                rounded-xl
                text-sm
                font-medium
                break-words
                ${
                  completed
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-yellow-50 text-yellow-700 border border-yellow-200"
                }
              `}
              >
                {user.name}
              </div>
            );
          })
        ) : (
          <div className="text-slate-400 text-sm">No users assigned</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col pt-7 pl-4 pr-7">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <IconFileText size={34} color="#1b6f8d" />

            <h2 className="text-3xl font-bold text-slate-800">
              {agreementData.wholeAgreement.titleOfAgreement}
            </h2>
          </div>

          <div className="flex items-center gap-5 mt-3 text-sm text-slate-500">
            <span>Contract #{agreementData.agreementId}</span>

            <span>
              Created on {formatDate(agreementData.wholeAgreement.createdAt)}
            </span>

            <span>
              &nbsp;at&nbsp;{formatTime(agreementData.wholeAgreement.createdAt)}
            </span>
          </div>
        </div>
        {agreementData.actionText === "force_closed" && (
          <Text mt="sm" size="xs" color="orange">
            this agreement has been closed on{" "}
            <strong>
              {formatDate(agreementData.wholeAgreement.closeDate)}
            </strong>
            {agreementData.wholeAgreement.closeReason && (
              <>
                {" "}
                — reason:{" "}
                <strong>{agreementData.wholeAgreement.closeReason}</strong>
              </>
            )}
          </Text>
        )}
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {(currentUser.loginAs.id === 1 ||
              currentUser.loginAs.id === 2 ||
              currentUser.loginAs.id === 4) && (
              <Button
                disabled={
                  agreementData.agreementStatus === "SENT" ||
                  agreementData.agreementStatus === "SIGNED" ||
                  agreementData.agreementStatus === "COMPLETED" ||
                  agreementData.actionText === "force_closed" ||
                  agreementData.agreementStatus === "FORCECLOSED"
                }
                radius="md"
                variant="outline"
                onClick={() => {
                  navigate("/app/agreements_manager/agreements/edit", {
                    state: {
                      agreementId,
                    },
                  });
                }}
              >
                <IconEdit size={16} />
                <span style={{ marginLeft: 5 }}>Edit</span>
              </Button>
            )}

            {(currentUser.loginAs.id === 1 ||
              currentUser.loginAs.id === 2 ||
              currentUser.loginAs.id === 4) &&
              (agreementData?.actionText === "force_closed" ||
                agreementData?.agreementStatus === "SIGNED" ||
                agreementData?.agreementStatus === "FORCECLOSED") && (
                <Button
                  radius="md"
                  color="orange"
                  disabled={agreementData.actionText === "force_closed"}
                  onClick={() => setCloseModalOpen(true)}
                >
                  <IconLock size={16} />
                  <span style={{ marginLeft: 5 }}>
                    {agreementData.actionText === "force_closed"
                      ? "Closed"
                      : "Close"}
                  </span>
                </Button>
              )}
            {(currentUser.loginAs.id === 1 ||
              currentUser.loginAs.id === 2 ||
              currentUser.loginAs.id === 4) &&
              agreementData?.actionText !== "sent_to_doc_locker" && (
                <Button radius="md" color="red" onClick={() => setOpen(true)}>
                  <IconTrash size={16} />
                  <span style={{ marginLeft: 5 }}>Delete</span>
                </Button>
              )}
          </div>
        </div>
      </div>
      <Modal
        opened={showEditSignatoryModal}
        onClose={() => setShowEditSignatoryModal(false)}
      >
        <EditSignatoryDetails
          agreementData={agreementData}
          setShowEditSignatoryModal={setShowEditSignatoryModal}
        />
      </Modal>
      <div className="flex flex-row justify-between items-center mt-2">
        <div className="flex items-center flex-wrap gap-3 mb-6">
          {agreementData.wholeAgreement.typeOfAgreement === "eStamp" ? (
            <Badge
              size="lg"
              radius="xl"
              variant="light"
              className="shadow-sm"
              color={agreementData.wholeAgreement.typeOfAgreement}
            >
              {getStatusText(agreementData.wholeAgreement.typeOfAgreement)}
            </Badge>
          ) : null}

          <Badge
            size="lg"
            radius="xl"
            variant="light"
            className="shadow-sm"
            color={agreementData.agreementStatus}
          >
            {getStatusText(agreementData.agreementStatus)}
          </Badge>

          {agreementData.agreementStatus !== "COMPLETED" &&
            agreementData.agreementStatus !== "COMPLETED" &&
            !agreementData.data.json.isLegacy &&
            agreementData?.data?.json?.isLegacy !== undefined && (
              <Text size="sm" className="bg-slate-100 px-3 py-1 rounded-full">
                Pending since{" "}
                <strong>{agreementData.noOfCompletionDays}</strong> days
              </Text>
            )}
        </div>

        {/* <div>
          {agreementData.wholeAgreement.typeOfAgreement === "eStamp" ? (
            <Badge
              style={{
                borderRadius: "16px",
                // color: '#121212',
                padding: "3px 8px",
                width: "170px",
                height: "33px",
                color: "bule",
                marginRight: "5px",
              }}
              color={[agreementData.wholeAgreement.typeOfAgreement] || "blue"}
            >
              {agreementData.wholeAgreement.typeOfAgreement}
            </Badge>
          ) : null}
          <Badge
            style={{
              borderRadius: "16px",
              // color: '#121212',
              padding: "3px 8px",
              width: "170px",
              height: "33px",
            }}
            color={agreementData.agreementStatus}
          >
            {getStatusText(agreementData.agreementStatus)}
          </Badge>

          {agreementData.agreementStatus !== "COMPLETED" &&
          !agreementData.data.json.isLegacy &&
          agreementData?.data?.json?.isLegacy !== undefined ? (
            <div className="mt-2 justify-center ml-4">
              {`pending since ${agreementData.noOfCompletionDays} days`}
            </div>
          ) : null}
        </div> */}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className={styles.title}>Contract Meta Information</div>
        <ContractDetail
          jobOrder={agreementData.wholeAgreement.jobOrder}
          typeOfAgreement={agreementData.wholeAgreement.typeOfAgreement}
          termOfContractInDays={agreementData.wholeAgreement.contractTermInDays}
          dateOfContract={agreementData.wholeAgreement.dateOfContract}
          // endDate={contractEndDate}
          endDate={agreementData.wholeAgreement.dueDateOfContract}
          location={agreementData.wholeAgreement.mapBusinessunit}
        />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className={styles.title}>First Party</div>
        <PartyDetail
          party={agreementData.wholeAgreement.firstParty}
          agreementData={agreementData}
        />
        {/* <AddressRenderer {...firstParty} /> */}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className={styles.title}>Second Party</div>
        {/* <AddressRenderer {...secondParty} /> */}
        <PartyDetail
          party={agreementData.wholeAgreement.secondParty}
          agreementData={agreementData}
        />
      </div>
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.thirdParty.indi_name ||
          agreementData.wholeAgreement.thirdParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Third Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.thirdParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.fourthParty.indi_name ||
          agreementData.wholeAgreement.fourthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Fourth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.fourthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.fifthParty.name ||
          agreementData.wholeAgreement.fifthParty.indi_name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Fifth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.fifthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.sixthParty?.indi_name ||
          agreementData.wholeAgreement.sixthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Sixth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.sixthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.seventhParty?.indi_name ||
          agreementData.wholeAgreement.seventhParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Seventh Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.seventhParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.eighthParty?.indi_name ||
          agreementData.wholeAgreement.eighthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Eighth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.eighthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.ninthParty?.indi_name ||
          agreementData.wholeAgreement.ninthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Ninth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.ninthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.tenthParty?.indi_name ||
          agreementData.wholeAgreement.tenthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Tenth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.tenthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.eleventhParty?.indi_name ||
          agreementData.wholeAgreement.eleventhParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Eleventh Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.eleventhParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.twelthParty?.indi_name ||
          agreementData.wholeAgreement.twelthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Twelth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.twelthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.thirteenthParty?.indi_name ||
          agreementData.wholeAgreement.thirteenthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Thirteenth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.thirteenthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.fourteenthParty?.indi_name ||
          agreementData.wholeAgreement.fourteenthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Fourteenth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.fourteenthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.fifteenthParty?.indi_name ||
          agreementData.wholeAgreement.fifteenthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Fifteenth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.fifteenthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.sixteenthParty?.indi_name ||
          agreementData.wholeAgreement.sixteenthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Sixteenth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.sixteenthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.seventeenthParty?.indi_name ||
          agreementData.wholeAgreement.seventeenthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Seventeenth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.seventeenthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.eighteenthParty?.indi_name ||
          agreementData.wholeAgreement.eighteenthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Eighteenth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.eighteenthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.nineteenthParty?.indi_name ||
          agreementData.wholeAgreement.nineteenthParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Nineteenth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.nineteenthParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {agreementData.wholeAgreement.twentythParty &&
        (agreementData.wholeAgreement.twentythParty?.indi_name ||
          agreementData.wholeAgreement.twentythParty.name) && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className={styles.title}>Twentyth Party</div>
            {/* <AddressRenderer {...secondParty} /> */}
            <PartyDetail
              party={agreementData.wholeAgreement.twentythParty}
              agreementData={agreementData}
            />
          </div>
        )}
      {/* <div className="flex flex-row justify-end">
      // initial edit delete button
      </div> */}

      {!agreementData.data.json.isLegacy && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <IconUsers size={30} color="#1b6f8d" />

            <h2 className="text-3xl font-bold text-slate-800">User Roles</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            <RoleCard title="Initiator" users={initiators} flowKey="3" />

            <RoleCard title="Editor" users={editors} flowKey="4" />

            <RoleCard title="Reviewer" users={reviewers} flowKey="5" />

            <RoleCard title="Approver" users={approvers} flowKey="6" />

            <RoleCard title="Signing Authority" users={signAuths} flowKey="7" />
          </div>
        </div>
      )}
      <Modal
        overflow="inside"
        closeOnClickOutside={false}
        size="calc(30vw)"
        opened={open}
        onClose={() => setOpen(!open)}
        title="Delete Contract"
      >
        <div className="my-2">
          <Text className="mb-4 text-center">
            Are you sure you want to delete this agreement?
          </Text>
          <div className="flex items-center gap-3">
            <Button
              style={{
                backgroundColor: colors.primary,
                borderRadius: "0.5rem",
                color: "#F5F5F5",
                // width: '180px',
              }}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={deleteLoading}
              className="ml-4"
              // style={{
              //   backgroundColor: deleteLoading ? "#ffcccc" : "#ff4d4d",
              //   borderRadius: "0.5rem",
              //   color: "#F5F5F5",
              //   // width: '180px',
              // }}
              onClick={() => deleteHandler()}
            >
              {deleteLoading ? (
                <BeatLoader size={10} color={colors.rejected} />
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        </div>
      </Modal>
      <CloseAgreementModal
        opened={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        agreementData={agreementData}
      />
    </div>
  );
}

export default AgreementDetail;
